import { useEffect, useRef, useState, useCallback } from "react";
import { API_ORIGIN } from "./api";

export default function useWebSocket(path, onMessage) {
    const [connected, setConnected] = useState(false);
    const [error, setError] = useState(null);
    const socketRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const reconnectDelayRef = useRef(1000);
    const onMessageRef = useRef(onMessage);

    onMessageRef.current = onMessage;

    const connect = useCallback(() => {
        if (socketRef.current && socketRef.current.readyState !== WebSocket.CLOSED) {
            return;
        }

        const token = localStorage.getItem("mbs_auth_token");
        const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
        let wsOrigin = API_ORIGIN.replace(/^https?:/, wsProtocol);

        if (wsOrigin.includes("localhost") || wsOrigin.includes("127.0.0.1")) {
            wsOrigin = `${wsProtocol}//${window.location.host}`;
        }

        const wsUrl = `${wsOrigin}/ws/${path}?token=${token || ""}`;

        try {
            const ws = new WebSocket(wsUrl);
            socketRef.current = ws;

            ws.onopen = () => {
                setConnected(true);
                setError(null);
                reconnectDelayRef.current = 1000;
            };

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    onMessageRef.current?.(data);
                } catch {
                    onMessageRef.current?.(event.data);
                }
            };

            ws.onerror = (err) => {
                setError(err);
            };

            ws.onclose = () => {
                setConnected(false);
                if (reconnectTimeoutRef.current) {
                    clearTimeout(reconnectTimeoutRef.current);
                }
                reconnectTimeoutRef.current = setTimeout(() => {
                    reconnectDelayRef.current = Math.min(reconnectDelayRef.current * 2, 15000);
                    connect();
                }, reconnectDelayRef.current);
            };
        } catch (err) {
            setError(err);
        }
    }, [path]);

    useEffect(() => {
        connect();
        return () => {
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
            if (socketRef.current) {
                socketRef.current.onclose = null;
                socketRef.current.close();
            }
        };
    }, [connect]);

    const sendMessage = useCallback((msg) => {
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
            socketRef.current.send(typeof msg === "string" ? msg : JSON.stringify(msg));
            return true;
        }
        return false;
    }, []);

    return { connected, error, sendMessage };
}
